import * as Sentry from "@sentry/react";
import { SENTRY_CONSTANTS } from "@/constants/sentry";
import { NETWORK_DISCONNECT_PATTERNS } from "@/types/error";
import { isLocal } from "@/helpers/cookie.helper";

export interface SentryUserContext {
  userId?: string | number;
  companyId?: string | number;
  threadId?: string | number;
}

/**
 * Module-level cache for user context used in beforeSend hook.
 * 
 * Why this is acceptable:
 * - Client-side only (no SSR concerns)
 * - Single user per browser session
 * - beforeSend hook doesn't have access to Sentry scope
 * - Cleared on logout via clearSentryUserContext()
 * 
 * Note: For SSR apps, use Sentry.getCurrentScope() instead
 */
let userContextCache: SentryUserContext = {};

export const setSentryUserContext = (context: SentryUserContext) => {
  userContextCache = { ...userContextCache, ...context };
  Sentry.setTag('user_id', context.userId?.toString());
  Sentry.setTag('company_id', context.companyId?.toString());
  
  if (context.threadId) {
    Sentry.setContext("thread", {
      threadId: context.threadId,
    });
    Sentry.setTag("thread", context.threadId);
  }
};

export const clearSentryUserContext = () => {
  userContextCache = {};
  Sentry.setUser(null);
};

/**
 * Formats Sentry message with user context
 * @param type - Message type (CUSTOM, LOG, or ERROR)
 * @param userPrefix - Formatted user identifier
 * @param originalTitle - Original error/log message
 * @param threadId - Optional thread identifier (as string)
 */
const formatSentryMessage = (
  type: 'CUSTOM' | 'LOG' | 'ERROR' | 'WARNING',
  userPrefix: string,
  originalTitle: string,
  threadId?: string
): string => {
  if (type === 'CUSTOM' && threadId) {
    return `CUSTOM: ${userPrefix} | ${threadId}: ${originalTitle}`;
  }
  return `${type}: ${userPrefix}: ${originalTitle}`;
};

const getSentryMessageType = (
  event: Sentry.ErrorEvent
): 'CUSTOM' | 'LOG' | 'ERROR' | 'WARNING' => {

  if (event.tags?.custom === 'true') {
    return 'CUSTOM';
  }

  if (event.logger === 'console') {
    if (event.level === 'error') return 'ERROR';
    if (event.level === 'warning') return 'WARNING';
    if (event.level === 'log' || event.level === 'info') return 'LOG';
  }
  return 'ERROR';
};

const transformEventTitle = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
  const { userId, companyId, threadId } = userContextCache;
  const originalTitle = event.exception?.values?.[0]?.value || event.message || "Unknown Error";
  
  const userPrefix: string =
    userId && companyId
      ? `User ID - ${userId} and Company ID - ${companyId}`
      : userId
        ? `User ID - ${userId}`
        : companyId
          ? `Company ID - ${companyId}`
          : "Unknown";

  const messageType = getSentryMessageType(event);
  
  event.message = formatSentryMessage(messageType, userPrefix, originalTitle, threadId?.toString());
  
  if (event.exception?.values?.[0]) {
    event.exception.values[0].value = event.message;
  }
  
  return event;
};

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE;

  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking is disabled.");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    // SSE chat-stream network blips throw bare TypeError ("Failed to fetch" in Chrome, "network error" in Safari).
    // ignoreErrors runs before beforeSend and drops these events at the SDK boundary so they never reach Sentry.
    ignoreErrors: [...NETWORK_DISCONNECT_PATTERNS],
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay is intentionally NOT eager here — it drags @sentry-internal/replay
      // (~133KB raw) into the entry chunk. It is lazy-loaded from the Sentry CDN after
      // init on an idle callback (see below), which keeps replaysSessionSampleRate /
      // replaysOnErrorSampleRate working since those live in the root init options.
      Sentry.captureConsoleIntegration({
        levels: ["warn", "error"],
      }),
    ],
    // Quota/perf tradeoff knob: full tracing in prod is expensive both for the Sentry
    // quota and for runtime overhead. 0.2 samples 20% of transactions in prod.
    tracesSampleRate: environment === "production" ? 0.2 : 1.0,
    replaysSessionSampleRate: environment === "production" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    enabled: !isLocal,
    enableLogs: true,
    beforeSend(event) {
      try {
        const messageType = getSentryMessageType(event);

        // Check if event message contains any excluded text
        const eventMessage = event.message || event.exception?.values?.[0]?.value || "";
        const shouldExcludeByMessage = SENTRY_CONSTANTS.EXCLUDE.some(excludeText =>
          eventMessage.toLowerCase().includes(excludeText.toLowerCase())
        );
        const shouldExclude = shouldExcludeByMessage || messageType === 'LOG';

        if (shouldExclude) {
          return null; // Don't send this event to Sentry
        }

        return transformEventTitle(event);
      } catch (error) {
        console.error('Sentry beforeSend error:', error);
        return event; // Send original event if transformation fails
      }
    },
  });

  // Lazy-load Session Replay off the critical path. lazyLoadIntegration pulls the
  // replay bundle from the Sentry CDN, so it never lands in our entry chunk. The
  // maskAllText/blockAllMedia options are the same ones previously passed eagerly;
  // the session/error sample rates continue to apply from the root init options above.
  // Skip in local to avoid a needless CDN fetch (Sentry is disabled there anyway).
  if (!isLocal) {
    const loadReplay = () => {
      Sentry.lazyLoadIntegration("replayIntegration")
        .then((replayIntegration) => {
          Sentry.addIntegration(
            replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            })
          );
        })
        .catch(() => {});
    };

    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(loadReplay);
    } else {
      setTimeout(loadReplay, 0);
    }
  }
};
